import {MemberPhoneListItem} from "@/models/member-phone-list"
import {ColumnDef} from "@tanstack/react-table"
import {useEffect, useState} from "react"
import {useDebounce} from "@/hooks/use-debounce"
import {DataTable} from "@/components/data-table.tsx"

interface MemberPhoneListDataTableProps {
    columns: ColumnDef<MemberPhoneListItem, unknown>[]
    data: MemberPhoneListItem[]
    searchTerm?: string
}

export function MemberPhoneListDataTable({columns, data, searchTerm = ""}: MemberPhoneListDataTableProps) {
    const [filteredData, setFilteredData] = useState<MemberPhoneListItem[]>(data)

    const debouncedSearchTerm = useDebounce(searchTerm, 300)

    useEffect(() => {
        if (!debouncedSearchTerm.trim()) {
            setFilteredData(data)
            return
        }

        const filtered = data.filter(member => {
            const searchLower = debouncedSearchTerm.toLowerCase()

            const memberNoMatch = member.memberNo.toString().includes(debouncedSearchTerm)
            const nameMatch = `${member.firstName} ${member.lastName}`
                .toLowerCase()
                .includes(searchLower)
            const streetMatch = member.street?.toLowerCase().includes(searchLower)
            const districtMatch = member.district?.toLowerCase().includes(searchLower)
            const landlineMatch = member.landline?.includes(debouncedSearchTerm)
            const mobileMatch = member.mobile?.includes(debouncedSearchTerm)
            const emailMatch = member.email?.toLowerCase().includes(searchLower)

            return memberNoMatch || nameMatch || streetMatch || districtMatch
                || landlineMatch || mobileMatch || emailMatch
        })

        setFilteredData(filtered)
    }, [debouncedSearchTerm, data])

    return (
        <div className="space-y-4">
            <DataTable
                columns={columns}
                data={filteredData}
                resetPageKey={debouncedSearchTerm}
                data-testid="phone-list-table"
            />
        </div>
    )
}
